// VerifyEmailGate.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useNavigation } from 'context/NavigationContext';
import { useProgress } from 'context/ProgressContext';
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';

// Firebase
import { signOut } from 'firebase/auth';

// Routing
import { useNavigate } from 'react-router-dom';
// Utilities
import { SectionType } from 'utilities/Enums';
import { getSectionPath } from 'utilities/Routing';

// Services
import {
  EMAIL_VERIFICATION_RESEND_COOLDOWN_MS,
  maskEmailAddress,
  reloadAndCheckEmailVerified,
  sendAccountVerificationEmail,
} from 'services/auth/EmailVerificationService';
import { sendPasswordReset } from 'services/auth/PasswordResetService';
import { getUser, syncEmailVerifiedOnUserNode } from 'services/users/UserService';

// CSS
import './VerifyEmailGate.css';

// Components
import Button from 'components/button/Button';
import LogoSmall from 'components/logo/small/LogoSmall';
import { auth } from 'firebaseConfig';

const mapVerificationError = (error) => {
  const code = String(error?.code || '');

  if (code === 'auth/too-many-requests') {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }

  return error?.message || 'Unable to send a verification email right now. Please try again.';
};

const mapPasswordResetError = (error) => {
  const code = String(error?.code || '');

  if (code === 'auth/invalid-email') {
    return 'Please enter a valid email address.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }

  return 'Unable to send a reset email right now. Please try again.';
};

/**
 * VerifyEmailGate
 *
 * Blocks unverified users until they confirm email ownership or sign out.
 *
 * @returns {ReactNode} The rendered verification gate.
 */
const VerifyEmailGate = () => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const { showProgress, hideProgress } = useProgress();
  const { setNewUserProfileSetup } = useUI();
  const { postAuthRedirect, setPostAuthRedirect, setCurrentSection } = useNavigation();
  const {
    authUser,
    setUser,
    user,
  } = useUser();

  // ----- ROUTING ----- //

  const navigate = useNavigate();

  // ----- STATE ----- //

  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [resendAvailableAt, setResendAvailableAt] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // ----- MEMOS ----- //

  const maskedEmail = useMemo(() => {
    return maskEmailAddress(authUser?.email || user?.data_profile?.email || user?.email);
  }, [authUser?.email, user?.data_profile?.email, user?.email]);

  const resendSecondsRemaining = useMemo(() => {
    const remainingMs = resendAvailableAt - Date.now();
    if (remainingMs <= 0) return 0;
    return Math.ceil(remainingMs / 1000);
  }, [resendAvailableAt]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (resendSecondsRemaining <= 0) return undefined;

    const timeoutId = window.setTimeout(() => {
      setResendAvailableAt((value) => value);
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [resendSecondsRemaining]);

  // ----- MEMOS ----- //

  const finishVerifiedEntry = useCallback(async () => {
    const uid = authUser?.uid || user?.key || user?.userKey || null;
    if (!uid) {
      throw new Error('Unable to load your account after verification.');
    }

    await syncEmailVerifiedOnUserNode(uid);

    const refreshedUser = await getUser(uid);
    if (refreshedUser) {
      setUser({
        ...refreshedUser,
        emailVerified: true,
      });
    }

    setNewUserProfileSetup(true);

    const targetPath = postAuthRedirect;
    if (targetPath) {
      setPostAuthRedirect(null);
      navigate(targetPath, { replace: true });
      return;
    }

    setCurrentSection(SectionType.HOME);
    navigate(getSectionPath(SectionType.HOME), { replace: true });
  }, [
    authUser?.uid,
    navigate,
    postAuthRedirect,
    setCurrentSection,
    setNewUserProfileSetup,
    setPostAuthRedirect,
    setUser,
    user,
  ]);

  // ----- HANDLERS ----- //

  /**
   * handleResendVerification
   *
   * Handles resend verification.
   * @returns {Promise<void>} Resolves when the handler completes.
   */
  const handleResendVerification = async () => {
    if (resendSecondsRemaining > 0) return;

    try {
      setErrorMessage('');
      setStatusMessage('');
      showProgress('Sending verification email...');
      await sendAccountVerificationEmail(authUser);
      setResendAvailableAt(Date.now() + EMAIL_VERIFICATION_RESEND_COOLDOWN_MS);
      setStatusMessage('Verification email sent. Check your inbox and spam folder.');
    } catch (error) {
      setErrorMessage(mapVerificationError(error));
    } finally {
      hideProgress();
    }
  };

  /**
   * handleContinue
   *
   * Handles continue.
   * @returns {Promise<void>} Resolves when the handler completes.
   */
  const handleContinue = async () => {
    try {
      setErrorMessage('');
      setStatusMessage('');
      showProgress('Checking verification status...');

      const verified = await reloadAndCheckEmailVerified(authUser);
      if (!verified) {
        setErrorMessage('Your email is not verified yet. Click the link in your inbox, then try again.');
        return;
      }

      await finishVerifiedEntry();
    } catch (error) {
      setErrorMessage(error?.message || 'Unable to confirm verification right now. Please try again.');
    } finally {
      hideProgress();
    }
  };

  /**
   * handleSendPasswordReset
   *
   * Handles send password reset.
   * @returns {Promise<void>} Resolves when the handler completes.
   */
  const handleSendPasswordReset = async () => {
    const email = String(authUser?.email || '').trim().toLowerCase();
    if (!email) {
      setErrorMessage('No email address is available for this account.');
      return;
    }

    try {
      setErrorMessage('');
      setStatusMessage('');
      showProgress('Sending reset link...');
      await sendPasswordReset(email);
      setForgotSent(true);
      setStatusMessage('If an account exists for that email, we sent a password reset link.');
    } catch (error) {
      setErrorMessage(mapPasswordResetError(error));
    } finally {
      hideProgress();
    }
  };

  /**
   * handleSignOut
   *
   * Handles sign out.
   * @returns {Promise<void>} Resolves when the handler completes.
   */
  const handleSignOut = async () => {
    await signOut(auth);
  };

  // ----- RENDER ----- //

  return (
    <div
      className="verify-email-gate-container"
    >

      {/* CARD */}
      <div className="verify-email-gate-card">
        <LogoSmall width="220px" />

        {/* TITLE */}
        <div
          className="verify-email-gate-title"
        >
          Verify your email to continue
        </div>

        {/* COPY */}
        <div
          className="verify-email-gate-copy"
        >
          We sent a verification link to{' '}

          {/* EMAIL */}
          <span
            className="verify-email-gate-email"
          >
            {maskedEmail}
          </span>
          . Open the link, then return here and continue.
        </div>

        {/* MESSAGE */}
        {statusMessage && (

          <div
            className="verify-email-gate-message"
          >
            {statusMessage}
          </div>
        )}

        {/* MESSAGE */}
        {errorMessage && (

          <div
            className="verify-email-gate-message"
            style={{
              color: theme.danger || '#d14343',
            }}
          >
            {errorMessage}
          </div>
        )}

        {!showForgotPassword ? (

          <div className="verify-email-gate-actions">
            <Button
              label={resendSecondsRemaining > 0
                ? `Resend verification email (${resendSecondsRemaining}s)`
                : 'Resend verification email'}
              enabled={resendSecondsRemaining <= 0}
              onClick={handleResendVerification}
            />
            <Button
              label="I've verified — continue"
              onClick={handleContinue}
            />
          </div>
        ) : (

          <div className="verify-email-gate-forgot-panel">

            {/* COPY */}
            <div
              className="verify-email-gate-copy"
            >
              We can send a password reset link to {maskedEmail}. You will still need to verify your email before entering Ai.
            </div>

            {/* MESSAGE */}
            {forgotSent ? (

              <div
                className="verify-email-gate-message"
              >
                If an account exists for that email, we sent a reset link. Check your inbox and spam folder.
              </div>
            ) : (
              <Button
                label="Send password reset link"
                onClick={handleSendPasswordReset}
              />
            )}
          </div>
        )}

        {/* LINKS */}
        <div className="verify-email-gate-links">

          {/* LINK */}
          <span
            className="verify-email-gate-link"
            onClick={() => {
              setShowForgotPassword((value) => !value);
              setForgotSent(false);
              setErrorMessage('');
              setStatusMessage('');
            }}
          >
            {showForgotPassword ? 'Back to verification' : 'Forgot password?'}
          </span>

          {/* LINK */}
          <span
            className="verify-email-gate-link"
            onClick={handleSignOut}
          >
            Sign out
          </span>
        </div>
      </div>
    </div>
  );
};

// Dev Tools
VerifyEmailGate.displayName = 'VerifyEmailGate';

export default VerifyEmailGate;
